import React, { useRef, useEffect, useCallback, useState } from 'react';

// Extend Window interface for webkitSpeechRecognition
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

interface UseSpeechToTextOptions {
  onResult: (text: string) => void;
  onSilenceTimeout?: () => void; // auto stop when silence after N seconds
  lang?: string;
  silenceTimeoutMs?: number; // auto stop when silence after N seconds
  maxDurationMs?: number;    // auto stop after total N seconds
  onError?: (message: string) => void;
}

interface UseSpeechToTextReturn {
  start: () => void;
  stop: () => void;
  stopAndDiscard: () => void;
  isListening: boolean;
  isSupported: boolean;
}

/**
 * Hook STT - User nói → Text
 * Sử dụng Web Speech API native với tiếng Việt
 * 
 * Features:
 * - Auto-stop sau silenceTimeoutMs (default 3s) khi user không nói
 * - Continuous mode cho đến khi bị stop hoặc timeout
 */
export function useSpeechToText(options: UseSpeechToTextOptions): UseSpeechToTextReturn {
  const { 
    onResult, 
    onSilenceTimeout,
    lang = 'vi-VN',
    silenceTimeoutMs = 3000,
    maxDurationMs = 10000,
    onError,
  } = options;
  
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const shouldStartRef = useRef(false);
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  
  // Silence timeout ref
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flag: đã fire silence timeout → block onresult để tránh duplicate
  const silenceTimeoutFiredRef = useRef(false);
  
  // Use refs for callbacks to avoid recreating recognition on every render
  const onResultRef = useRef(onResult);

  const onSilenceTimeoutRef = useRef(onSilenceTimeout);
  const onErrorRef = useRef(onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);
  

  useEffect(() => {
    onSilenceTimeoutRef.current = onSilenceTimeout;
  }, [onSilenceTimeout]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Reset silence timer helper
  const resetSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    silenceTimerRef.current = setTimeout(() => {
      console.log(`[STT] Silence timeout (${silenceTimeoutMs}ms) - auto stopping & sending`);
      // Đánh dấu đã timeout → block mọi onresult tiếp theo
      silenceTimeoutFiredRef.current = true;
      // Gọi onSilenceTimeout TRƯỚC khi stop → để caller auto-send
      onSilenceTimeoutRef.current?.();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    }, silenceTimeoutMs);
  }, [silenceTimeoutMs]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
        maxDurationTimerRef.current = null;
    }
  }, []);

  // Initialize recognition once
  useEffect(() => {
    const SpeechRecognitionAPI = 
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognitionAPI) {
      console.warn('[STT] Speech Recognition không được hỗ trợ');
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = lang;
    recognition.continuous = true; // Tiếp tục ghi âm cho đến khi user stop
    recognition.interimResults = true; // Hiển thị text realtime khi đang nói

    recognition.onstart = () => {
      console.log('[STT] Started listening...');
      setIsListening(true);
    };

    recognition.onend = () => {
      console.log('[STT] Stopped listening');
      setIsListening(false);
      clearSilenceTimer();
      clearMaxDurationTimer();
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // Nếu đã fire silence timeout → bỏ qua (tránh duplicate gửi)
      if (silenceTimeoutFiredRef.current) {
        console.log('[STT] Ignoring result after silence timeout');
        return;
      }
      // Lấy tất cả transcript từ các kết quả (continuous mode)
      let fullTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        fullTranscript += event.results[i][0].transcript;
      }
      console.log('[STT] Result:', fullTranscript);
      onResultRef.current(fullTranscript);
      
      // Reset silence timer mỗi khi có speech result
      resetSilenceTimer();
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('[STT] Error:', event.error);
      if (event.error === 'not-allowed') {
        onErrorRef.current?.("Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings");
      } else if (event.error === 'audio-capture') {
        onErrorRef.current?.("Không tìm thấy microphone trên thiết bị");
      }
      setIsListening(false);
      clearSilenceTimer();
    };

    // Khi speechend (user ngưng nói) → bắt đầu đếm silence timer
    recognition.onspeechend = () => {
      console.log('[STT] Speech ended, starting silence timer');
      resetSilenceTimer();
    };

    recognitionRef.current = recognition;

    return () => {
      clearSilenceTimer();
      clearMaxDurationTimer();
      shouldStartRef.current = false;
      recognitionRef.current?.abort();
    };
  }, [lang, resetSilenceTimer, clearSilenceTimer]);

  const start = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      onErrorRef.current?.("Trình duyệt không hỗ trợ ghi âm");
      return;
    }

    if (recognitionRef.current && !isListening) {
      shouldStartRef.current = true;
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then((stream) => {
          // We only check for permission/availability, stop stream immediately
          stream.getTracks().forEach(track => track.stop());
          
          if (!shouldStartRef.current) {
            console.log('[STT] getUserMedia resolved but STT was stopped/cancelled in the meantime');
            return;
          }
          
          try {
            // Reset flag cho phiên mới
            silenceTimeoutFiredRef.current = false;
            if (recognitionRef.current) {
              recognitionRef.current.start();
              // Start silence timer ngay khi bắt đầu listening
              resetSilenceTimer();
              
              // Start max duration timer
              if (maxDurationMs > 0) {
                maxDurationTimerRef.current = setTimeout(() => {
                    console.log(`[STT] Max duration reached (${maxDurationMs}ms) - auto stopping & sending`);
                    silenceTimeoutFiredRef.current = true;
                    onSilenceTimeoutRef.current?.();
                    if (recognitionRef.current) {
                        recognitionRef.current.stop();
                    }
                }, maxDurationMs);
              }
            }
          } catch (error) {
            console.error('[STT] Error starting:', error);
          }
        })
        .catch((err) => {
          if (!shouldStartRef.current) {
            console.log('[STT] getUserMedia failed but STT was already stopped/cancelled');
            return;
          }
          console.error('[STT] getUserMedia error:', err);
          if (err.name === 'NotAllowedError') {
            onErrorRef.current?.("Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings");
          } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
            onErrorRef.current?.("Không tìm thấy microphone trên thiết bị");
          } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
            onErrorRef.current?.("Không thể kích hoạt micro khi một ứng dụng khác đang dùng micro");
          } else {
            onErrorRef.current?.("Không thể kích hoạt micro");
          }
        });
    }
  }, [isListening, resetSilenceTimer, maxDurationMs]);

  const stop = useCallback(() => {
    shouldStartRef.current = false;
    clearSilenceTimer();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('[STT] Error stopping recognition:', e);
      }
    }
    setIsListening(false);
  }, [clearSilenceTimer]);

  // Stop và block mọi onresult tiếp theo (dùng khi send manual hoặc tắt chatbot để tránh text bị ghi đè/mic bị treo)
  const stopAndDiscard = useCallback(() => {
    shouldStartRef.current = false;
    silenceTimeoutFiredRef.current = true; // Block onresult
    clearSilenceTimer();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort(); // Use abort for immediate stop without waiting for final result
      } catch (e) {
        console.error('[STT] Error aborting recognition:', e);
      }
    }
    setIsListening(false); // Force state update immediately
  }, [clearSilenceTimer]);

  return {
    start,
    stop,
    stopAndDiscard,
    isListening,
    isSupported,
  };
}

export default useSpeechToText;
